/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAccessToken,
  selectRefreshToken,
  selectTokenRevocation,
} from 'modules/common/auth/selectors';
import { isTokenExpired } from 'modules/common/helpers/jwt';
import request from 'modules/common/utils/request';
import API from 'modules/sign-in/constants';
import TIME_OUTS from 'modules/common/constants/time-outs';

const useIsAuthenticated = () => {
  const refreshToken = useSelector(selectRefreshToken);
  const accessToken = useSelector(selectAccessToken);
  const isTokenRevoked = useSelector(selectTokenRevocation);
  const isAuthenticated = useCallback(
    () => !isTokenRevoked && !isTokenExpired(refreshToken),
    [refreshToken, isTokenRevoked]
  );
  //
  /**
   * Asks the provider whether the access token is still active.
   * Resolves true when the access token is missing or about to expire (the
   * refresh flow replaces it and the check runs again), when introspection is
   * unavailable (network error or timeout, reported as status null or 0, or a
   * 5xx response), or when the check was canceled.
   * Resolves false when the provider reports it inactive or rejects the
   * request with a client error.
   */
  const checkTokenActivity = useCallback(
    async (signal) => {
      if (isTokenExpired(accessToken, TIME_OUTS.ACCESS_TOKEN_REFRESH_LEEWAY)) return true;
      try {
        const data = await request(API.POST_TOKEN_INFO, { token: accessToken }, false, false, {
          signal,
        });
        return Boolean(data?.active);
      } catch (error) {
        // A newer check superseded this one; the caller ignores canceled results.
        if (error?.isCanceled) return true;
        // Unavailable introspection (network error, timeout, status 0 or 5xx)
        // preserves the locally valid session.
        const status = error?.status;
        return status == null || status === 0 || status >= 500;
      }
    },
    [accessToken]
  );
  //
  const checkAuthentication = useCallback(
    (candidateRefreshToken) => !isTokenExpired(candidateRefreshToken),
    []
  );
  //
  return { isAuthenticated, checkAuthentication, checkTokenActivity, isTokenRevoked };
};
//
export default useIsAuthenticated;
